import * as Sentry from '@sentry/browser'
import { useMutation } from '@tanstack/react-query'
import {
  uploadFile,
  cadVersionUploadedAsync,
  createDocumentPageThemeUserDefault,
  updateDocumentPageThemeUserDefault,
} from '@/lib/api/client'
import { invalidateProjects } from '@/services/queries/projects/cache'
import DOCUMENT_QUERY_KEYS from '@/services/queries/documents/queryKeys'
import { QUERY_KEYS as DOCUMENT_THEMES_USER_DEFAULTS_QUERY_KEY } from '@/services/queries/document_themes_user_defaults/queryKeys'
import { compressFile } from '@/lib/gzip'
import queryClient from '@/queryClient'
import { useToast } from '@/components/ui/use-toast'
import CustomError from '@/lib/api/CustomError'
import { track } from '@/utils/amplitude'

import { useCreateProject } from '@/services/queries/projects'
import { useCreateCad } from '@/services/queries/cads'
import { useCreateCadVersion } from '@/services/queries/cad_versions'
import { useCreateDocument } from '@/services/queries/documents'
import { useCreateDocumentVersion } from '@/services/queries/document_versions'
import { useDocumentPageThemes } from '@/services/queries/document_themes'

type FormInputs = {
  documentName: string
  themeId: string
  cadId?: string
  files?: File[]
  projectId?: string
  projectName?: string
}

interface CreateDocumentMutationProps {
  onSuccess: (props: {
    documentId: string
    projectId: string
    cadVersionId?: string
  }) => void
}

export const useCreateDocumentFormMutation = ({
  onSuccess,
}: CreateDocumentMutationProps) => {
  const { toast } = useToast()
  const { mutateAsync: createProject } = useCreateProject()
  const { mutateAsync: createCad } = useCreateCad()
  const { mutateAsync: createCadVersion } = useCreateCadVersion()
  const { mutateAsync: createDocument } = useCreateDocument()
  const { mutateAsync: createDocumentVersion } = useCreateDocumentVersion()

  const { data: themes } = useDocumentPageThemes()

  const defaultProjectName = 'Q20 Project'

  const createAndUploadNewCAD = async (projectId: string, files: File[]) => {
    const file = files[0]
    const { cadId } = await createCad({
      projectId,
      values: { file: file, name: file.name },
    })

    const compressedFile = await compressFile(file)
    const { cadVersion, uploadUrl } = await createCadVersion({
      projectId,
      cadId,
      values: { filename: compressedFile.name, comment: '' },
    })

    await uploadFile(uploadUrl, compressedFile)

    await cadVersionUploadedAsync(cadVersion.id || '')

    return { cadId, cadVersionId: cadVersion.id as string }
  }

  return useMutation({
    mutationFn: async ({
      projectName,
      formValues,
      isCreatingNewProject,
      isUploadingNewCAD,
      userThemeDefaultId,
    }: {
      projectName?: string
      formValues: FormInputs
      isCreatingNewProject: boolean
      isUploadingNewCAD: boolean
      userThemeDefaultId?: number | null
    }) => {
      const projectId =
        isCreatingNewProject && projectName
          ? (await createProject({ name: projectName })).id || ''
          : formValues.projectId || defaultProjectName

      if (isCreatingNewProject && projectName) {
        track('Project Created', {
          projectName,
          projectId,
        })
      }

      //
      // Set the default theme for a user. This will be the document page theme that
      // is used by default when creating new operation pages
      //
      const themeId = formValues.themeId

      const selectedTheme = themes?.find((theme) => theme.id === themeId)

      if (selectedTheme) {
        if (userThemeDefaultId) {
          await updateDocumentPageThemeUserDefault(userThemeDefaultId, {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            document_page_theme_id: selectedTheme.id as string,
          })
        } else {
          await createDocumentPageThemeUserDefault({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            document_page_theme_id: selectedTheme.id as string,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            theme_type: selectedTheme.theme_type as any,
          })
        }
      } else {
        throw new Error('Failed to set default page theme')
      }

      const { cadId, cadVersionId } =
        isUploadingNewCAD && formValues.files
          ? await createAndUploadNewCAD(projectId, formValues.files)
          : { cadId: formValues.cadId || '' }

      const documentId =
        (await createDocument({
          projectId,
          values: {
            name: formValues.documentName,
            cad_id: cadId,
            document_type: 'work_instructions',
          },
        }).then(({ document }) => document.id)) || ''

      await createDocumentVersion({ documentId })

      if (isUploadingNewCAD && formValues.files) {
        track('CAD Created', {
          cadId,
          projectId,
        })
      }

      if (formValues.documentName && projectName) {
        track('Document Created', {
          documentName: formValues.documentName,
          documentId,
          projectId,
          projectName,
        })
      }

      return {
        projectId,
        documentId,
        cadVersionId,
      }
    },
    onError: (err) => {
      if (err instanceof CustomError) {
        toast({
          title: 'Error',
          description: err.message,
          variant: 'destructive',
        })
        Sentry.captureException(err)
      }
    },
    onSuccess: async (props) => {
      await invalidateProjects()
      await queryClient.invalidateQueries({
        queryKey: [
          DOCUMENT_QUERY_KEYS.DOCUMENTS,
          { projectId: props.projectId },
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [
          DOCUMENT_THEMES_USER_DEFAULTS_QUERY_KEY.DOCUMENT_THEMES_USER_DEFAULTS,
        ],
        refetchType: 'all',
      })
      if (onSuccess) onSuccess(props)
    },
  })
}
